import { css, keyframes } from '@emotion/react'
import { hexToRGB, Text, Theme, useTheme } from 'bold-ui'
import { ReactNode } from 'react'

interface MapFeedbackProps {
  children: ReactNode
  isLoading: boolean
}

export function MapFeedback(props: Readonly<MapFeedbackProps>) {
  const { children, isLoading } = props

  const theme = useTheme()
  const styles = createStyles(theme)

  if (!isLoading && children == null) return null

  const renderLoading = () => (
    <span key='loading' css={styles.content}>
      <span data-testid='map-loading-spinner' css={styles.spinner} aria-hidden='true' />
      <span css={styles.text}>
        <Text variant='h4' fontWeight='bold' color='inherit'>
          Carregando...
        </Text>
      </span>
    </span>
  )

  const renderChildren = () => (
    <span key='mapFeedback' css={styles.content}>
      <span css={styles.text}>{children}</span>
    </span>
  )

  return (
    <output css={styles.root} aria-live='polite' aria-atomic='true'>
      {isLoading ? renderLoading() : renderChildren()}
    </output>
  )
}

const MOTION_DURATION = '300ms'
const MOTION_EASING = 'cubic-bezier(0.1, 0.4, 0.15, 1)'

const scaleIn = keyframes`
  from {
    transform: scale(0);
  }
  to {
    transform: scale(1);
  }
`

const fadeIn = keyframes`
  from {
    opacity: 0;
  }
  to {
    opacity: 1;
  }
`

const spin = keyframes`
  to {
    transform: rotate(360deg);
  }
`

const createStyles = (theme: Theme) => ({
  root: css`
    position: absolute;
    top: 1rem;
    left: 50%;
    z-index: ${theme.zIndex.popper};
    width: max-content;
    max-width: calc(100% - 2rem);
    transform: translateX(-50%);
    pointer-events: none;
  `,
  content: css`
    display: flex;
    gap: 0.25rem;
    align-items: center;
    padding: 0.5rem;
    transform-origin: center;
    border-radius: ${theme.radius.button}px;
    background-color: ${hexToRGB(theme.pallete.gray.c20, 0.85)};
    white-space: nowrap;
    animation-name: ${scaleIn};
    animation-duration: ${MOTION_DURATION};
    animation-timing-function: ${MOTION_EASING};
    animation-fill-mode: both;

    @media (prefers-reduced-motion: reduce) {
      animation: none;
    }
  `,
  text: css`
    display: flex;
    gap: 0.25rem;
    color: ${theme.pallete.surface.main};
    animation-name: ${fadeIn};
    animation-duration: ${MOTION_DURATION};
    animation-timing-function: ${MOTION_EASING};
    animation-fill-mode: both;

    @media (prefers-reduced-motion: reduce) {
      animation: none;
    }
  `,
  spinner: css`
    flex: 0 0 auto;
    width: 1rem;
    height: 1rem;
    margin-right: 0.25rem;
    border: 2px solid ${theme.pallete.surface.main};
    border-right-color: transparent;
    border-radius: 50%;
    animation-name: ${spin};
    animation-duration: 450ms;
    animation-timing-function: linear;
    animation-iteration-count: infinite;
  `,
})
